import { useState, useCallback, useEffect } from "react";

const withMsg = (id, msg) => ({ id, msg });

export default function useEmployeesValidation(employees = []) {
  const [errors, setErrors] = useState([]);

  /**
   *  Pure function that scans the *current* employee list and returns an array
   *  of error objects: `{ id, msg }`.  An `id` of `"*"` designates a global
   *  issue not tied to a single row.
   */
  const computeErrors = useCallback(() => {
    const next = [];

    /* ───── Duplicate detection ───── */
    const seen = {
      userIds: new Set(),
      bizIds: new Set(),
      names: new Set(),
      emails: new Set(),
    };

    const dupeCheck = (key, val, id, label) => {
      if (!val) return;
      if (seen[key].has(val)) {
        next.push(withMsg(id, `Duplicate ${label}`));
      }
      seen[key].add(val);
    };

    employees.forEach((emp) => {
      const { id, identityType, linkedUser, linkedBusiness, fullName = "", email = "" } = emp;

      // linked user / business uniqueness
      if (identityType === "USER") dupeCheck("userIds", linkedUser?.id, id, "Trippin user");
      if (identityType === "BUSINESS") dupeCheck("bizIds", linkedBusiness?.id, id, "business account");

      // name / email uniqueness (case‑insensitive)
        if (identityType === "CUSTOM") {
            dupeCheck("names", fullName.trim().toLowerCase(), id, "full name");
            dupeCheck("emails", email.trim().toLowerCase(), id, "e‑mail");
        }
    });

      /* ───── Per‑employee validation ───── */
      employees.forEach((emp) => {
          const { id, roleType, identityType, picture, fullName = "", email = "", linkedUser, linkedBusiness } = emp;
          const nameTrim = fullName.trim();

          // ⚠️ Ensure a Trippin user or business is actually linked
          if (identityType === "USER" && !linkedUser) {
          next.push(withMsg(id, "Please link a Trippin user"));
          }
          if (identityType === "BUSINESS" && !linkedBusiness) {
          next.push(withMsg(id, "Please link a business account"));
          }
          // ① Picture requirements & cross‑type constraints
          if (roleType === "SERVICE_PROVIDER") {
              if (identityType === "BUSINESS") {
                  next.push(withMsg(id, "Service providers cannot be businesses"));
              }
          }

          // ② Custom employee e‑mail requirement
          if (identityType === "CUSTOM" && !email.trim()) {
              next.push(withMsg(id, "Custom employees require an e‑mail"));
          }

          // ③ Full‑name requirements
          if (identityType === "CUSTOM" && !nameTrim) {
              next.push(withMsg(id, "Full name is required"));
          } else if (nameTrim.length > 60) {
              next.push(withMsg(id, "Full name is too long (max 60 chars)"));
          }
      });

    return next;
  }, [employees]);

  /* ───────── Keep `errors` in‑sync with incoming data ───────── */
  useEffect(() => {
    setErrors(computeErrors());
  }, [employees]);

  /**
   * Explicit validation trigger – returns **true** when no errors exist.
   */
  const validateAll = () => {
    const next = computeErrors();
    setErrors(next);
    return next.length === 0;
  };

  return { errors, validateAll };
}

/**
 * Small UI helper: should the picture uploader be rendered for the given
 * employee row?  Encapsulating the rule here keeps component code tidy.
 */
export const showPictureUploader = (emp) => emp.roleType === "SERVICE_PROVIDER";

/**
 * Helper for inline hint text beneath the picture uploader.
 */
export const pictureHint =
  "If you want clients to choose their service‑provider, it helps to add a picture so we can show it to them.";
